/* =========================================================================
   ClueVine — deterministic PRNG
   xmur3 hashes a string seed into 32-bit state; sfc32 turns that into a fast,
   well-distributed stream. Same seed in → same sequence out, forever. This is
   what makes daily puzzles identical for everyone and any puzzle reproducible.
   No dependencies.
   ========================================================================= */

import type { Prng } from './types';

/** xmur3 — string → repeatable 32-bit seed generator. */
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function next() {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** sfc32 — small fast counter PRNG. Returns floats in [0, 1). */
function sfc32(a: number, b: number, c: number, d: number): () => number {
  return function next() {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

/** Build a deterministic PRNG from any string seed. */
export function createPrng(seed: string): Prng {
  const seedGen = xmur3(seed);
  const rand = sfc32(seedGen(), seedGen(), seedGen(), seedGen());
  // Discard the first values; sfc32 mixes better after a short warm-up.
  for (let i = 0; i < 15; i++) rand();

  return {
    next: rand,
    int(maxExclusive) {
      return Math.floor(rand() * maxExclusive);
    },
    range(min, maxInclusive) {
      return min + Math.floor(rand() * (maxInclusive - min + 1));
    },
    pick(arr) {
      return arr[Math.floor(rand() * arr.length)];
    },
    shuffle(arr) {
      const out = arr.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
      }
      return out;
    },
    bool(probability = 0.5) {
      return rand() < probability;
    },
  };
}

/** A stable 32-bit hash of a string — handy for compact seed identifiers. */
export function hashSeed(seed: string): number {
  return xmur3(seed)();
}
